// usePageDataStore.ts
import { PageStatus } from '@/types/store';
import merge from 'lodash/merge';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { DEFAULT_PRODUCT_DATA, defaultPageData } from './constants';
import { ProductPage, ServerPageData } from './types';

// Deep merge utility type for ProductPage
type DeepPartial<T> = T extends object
    ? {
          [P in keyof T]?: DeepPartial<T[P]>;
      }
    : T;

interface PageEditorState {
    // The product page data
    pageData: ProductPage;

    // Additional state flags
    status: PageStatus;
    slug: string | null;
    order: number;
    updatedAt?: number;
    loading: boolean;
    publishing: boolean;
    autoSaving: boolean;

    // Update entire page data or partial (supports deep partial updates)
    updatePageData: (page: DeepPartial<ProductPage> | ((prev: ProductPage) => ProductPage)) => void;

    getServerPageData: () => ServerPageData;

    // Load from existing ProductPage
    loadFromServerPageData: (page: ServerPageData, pageId: number | undefined) => void;

    // Initialize page for a specific product type
    initializePageType: (pageType: string) => void;

    setFlags: (
        p: Partial<
            Pick<PageEditorState, 'status' | 'slug' | 'order' | 'updatedAt' | 'loading' | 'publishing' | 'autoSaving'>
        >
    ) => void;
    reset: () => void;
}

export const usePageDataStore = create<PageEditorState>()(
    devtools((set, get) => ({
        pageData: defaultPageData,

        // TODO: PAVI - implement
        loading: true,
        publishing: false,
        autoSaving: false,

        updatePageData: (page: DeepPartial<ProductPage> | ((prev: ProductPage) => ProductPage)) => {
            // NOTE: Cannot handle array removals with lodash merge, use a function based approach instead
            set(
                {
                    pageData: typeof page === 'function' ? page(get().pageData) : merge({}, get().pageData, page),
                },
                false,
                'updatePageData'
            );
        },

        getServerPageData: () => {
            const { pageData } = get();

            return {
                order: pageData.metadata.order,
                status: pageData.metadata.status,
                page_id: pageData.metadata.pageId,
                location: pageData.metadata.location,
                top_banner_text: pageData.storePresentation.topBannerText || null,
                button: {
                    button_text: pageData.storePresentation.text,
                    tagline: pageData.storePresentation.tagline,
                    image: pageData.storePresentation.image,
                    layout: pageData.storePresentation.layout,
                    version: 'v2',
                },
                product: {
                    id: pageData.product.id,
                    type: pageData.product.type,
                    title: pageData.storePresentation.title,
                    description: pageData.productPresentation.description,
                    image: pageData.productPresentation.image,
                    slug: pageData.options.slug,
                    price: {
                        amount: pageData.product.price?.amount || 0,
                        currency: pageData.product.price?.currency || 'USD',
                        sale_amount_available: false,
                        discount_codes_available: pageData.product.price?.discountCodesAvailable || false,
                    },
                    reviews: pageData.options.reviews || [],
                    questions: pageData.product.questions || [],
                    inventory: {
                        stock: pageData.product.inventory?.stock || 0,
                        manage: pageData.product.inventory?.manage || false,
                        low_stock_amount: pageData.product.inventory?.lowStockAmount || 0,
                    },
                    emails: pageData.options.emails || {},
                },
                second_built_in_product: {
                    enabled: pageData.options.secondBuiltInProduct.enabled,
                    page_id: pageData.options.secondBuiltInProduct.enabled
                        ? pageData.options.secondBuiltInProduct.pageId
                        : -1,
                },
                affiliate_shares: [],
                resell_rights: {
                    enabled: pageData.options.resellRights.enabled,
                    percentage: pageData.options.resellRights.percentage,
                    affiliate_terms_and_condition: {
                        enabled: pageData.options.resellRights.affiliateTermsAndCondition?.enabled || false,
                        text: pageData.options.resellRights.affiliateTermsAndCondition?.text || '',
                    },
                },
                community_integration: {
                    enabled: pageData.options.communityIntegration.enabled,
                },
                countdown_timer: {},
            } as ServerPageData;
        },

        loadFromServerPageData: () => {
            // set(
            //     {
            //         pageData: {
            //             metadata: {
            //                 pageId: pageId || undefined,
            //                 location: page.location,
            //                 order: page.order,
            //                 status: page.status,
            //             },
            //             storePresentation: {
            //                 text: page.button.button_text,
            //                 image: page.button.image,
            //                 layout: page.button.layout,
            //                 tagline: page.button.tagline,
            //                 bottomTitle: '', // Not in server data
            //                 submitTitle: '', // Not in server data
            //                 topBannerText: page.top_banner_text || '',
            //                 title: page.product.title,
            //             },
            //             productPresentation: {
            //                 description: page.product.description,
            //                 image: page.product.image,
            //             },
            //             product: {
            //                 id: page.product.id,
            //                 type: page.product.type,
            //                 price: {
            //                     amount: page.product.price.amount,
            //                     currency: page.product.price.currency,
            //                     discountCodes: 0,
            //                     discountCodesAvailable: page.product.price.discount_codes_available,
            //                     membershipDurationAvailable: false,
            //                     stanFlexiblePayment: null,
            //                 },
            //                 inventory: {
            //                     manage: page.product.inventory.manage,
            //                     stock: page.product.inventory.stock,
            //                     lowStockAmount: page.product.inventory.low_stock_amount,
            //                 },
            //                 questions: page.product.questions || [],
            //             },
            //             options: {
            //                 reviews: page.product.reviews || [],
            //                 emailFlows: [],
            //                 secondBuiltInProduct: {
            //                     enabled: page.second_built_in_product.enabled,
            //                     pageId: page.second_built_in_product.page_id,
            //                     discountPercentage: 0,
            //                 },
            //                 resellRights: {
            //                     enabled: page.resell_rights.enabled,
            //                     percentage: page.resell_rights.percentage,
            //                     affiliateTermsAndCondition: {
            //                         enabled: page.resell_rights.affiliate_terms_and_condition?.enabled || false,
            //                         text: page.resell_rights.affiliate_terms_and_condition?.text || '',
            //                     },
            //                 },
            //                 communityIntegration: {
            //                     enabled: page.community_integration.enabled,
            //                 },
            //                 emails: page.product.emails || {},
            //                 currencyOverride: undefined,
            //                 slug: page.product.slug || '',
            //             },
            //         },
            //     },
            //     false,
            //     'loadFromServerPageData'
            // );
        },

        initializePageType: (pageType: string) => {
            const defaultPageData = DEFAULT_PRODUCT_DATA[pageType as keyof typeof DEFAULT_PRODUCT_DATA];
            if (!defaultPageData) {
                alert(`No default page data found for page type: ${pageType}`);
                return;
            }
            get().loadFromServerPageData(defaultPageData as ServerPageData, undefined);
        },

        setFlags: p => set(p, false, 'setFlags'),

        reset: () => set({ pageData: defaultPageData }, false, 'reset'),
    }))
);
